import { useTranslation } from 'react-i18next';
import { formatDate } from '@/lib/utils';
import { cn } from '@/lib/utils';
import {
  Package,
  CreditCard,
  CheckCircle2,
  Truck,
  MapPin,
  Clock,
  XCircle,
  RefreshCw,
  CircleDot,
  ExternalLink
} from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import type { OrderTracking, TrackingTimelineEvent } from '@/lib/types';

interface OrderTimelineProps {
  tracking: OrderTracking;
}

// Map event types to icons
const eventTypeIcons: Record<string, React.ElementType> = {
  ORDER_CREATED: Package,
  PAYMENT_PENDING: Clock,
  PAYMENT_CONFIRMED: CreditCard,
  ORDER_VALIDATED: CheckCircle2,
  PREPARATION_STARTED: RefreshCw,
  READY_FOR_PICKUP: MapPin,
  SHIPPED: Truck,
  IN_TRANSIT: Truck,
  OUT_FOR_DELIVERY: Truck,
  DELIVERED: CheckCircle2,
  CANCELED: XCircle,
  REFUNDED: RefreshCw,
  CUSTOM: CircleDot,
};

// Map event types to colors
const eventTypeColors: Record<string, string> = {
  ORDER_CREATED: 'text-blue-500 bg-blue-100',
  PAYMENT_PENDING: 'text-yellow-500 bg-yellow-100',
  PAYMENT_CONFIRMED: 'text-green-500 bg-green-100',
  ORDER_VALIDATED: 'text-green-500 bg-green-100',
  PREPARATION_STARTED: 'text-purple-500 bg-purple-100',
  READY_FOR_PICKUP: 'text-orange-500 bg-orange-100',
  SHIPPED: 'text-blue-500 bg-blue-100',
  IN_TRANSIT: 'text-blue-500 bg-blue-100',
  OUT_FOR_DELIVERY: 'text-blue-500 bg-blue-100',
  DELIVERED: 'text-green-500 bg-green-100',
  CANCELED: 'text-red-500 bg-red-100',
  REFUNDED: 'text-orange-500 bg-orange-100',
  CUSTOM: 'text-gray-500 bg-gray-100',
};

function TimelineEvent({
  event,
  isLast
}: {
  event: TrackingTimelineEvent;
  isLast: boolean;
}) {
  const { t } = useTranslation();
  const Icon = eventTypeIcons[event.eventType] || CircleDot;
  const colorClass = eventTypeColors[event.eventType] || 'text-gray-500 bg-gray-100';

  // Use translations with fallback to server-provided values
  // Server sends i18n keys like "tracking_order_created" as title
  const title = t(`orders.${event.title}`, {
    defaultValue: t(`orders.trackingEvents.${event.eventType}`, { defaultValue: event.title })
  });
  const description = t(`orders.${event.description}`, {
    defaultValue: t(`orders.trackingDescriptions.${event.eventType}`, { defaultValue: event.description })
  });

  return (
    <div className="relative flex gap-4">
      {/* Vertical line */}
      {!isLast && (
        <div
          className={cn(
            "absolute left-[19px] top-10 w-0.5 h-[calc(100%-16px)]",
            event.isCompleted ? "bg-primary" : "bg-muted"
          )}
        />
      )}

      {/* Icon */}
      <div
        className={cn(
          "relative z-10 flex items-center justify-center w-10 h-10 rounded-full shrink-0",
          event.isCompleted ? colorClass : "text-muted-foreground bg-muted",
          event.isCurrent && "ring-2 ring-primary ring-offset-2"
        )}
      >
        <Icon className="size-5" />
      </div>

      {/* Content */}
      <div className="flex-1 pb-6">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className={cn(
              "font-medium",
              event.isCurrent && "text-primary",
              !event.isCompleted && !event.isCurrent && "text-muted-foreground"
            )}>
              {title}
            </p>
            {description && (
              <p className="text-sm text-muted-foreground mt-0.5">
                {description}
              </p>
            )}
          </div>
          {event.isCompleted && (
            <Badge variant="outline" className="shrink-0 text-xs">
              {formatDate(event.actualDate || event.createdAt, 'dd/MM HH:mm')}
            </Badge>
          )}
          {!event.isCompleted && event.estimatedDate && (
            <Badge variant="secondary" className="shrink-0 text-xs">
              Est. {formatDate(event.estimatedDate, 'dd/MM')}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

export function OrderTimeline({ tracking }: OrderTimelineProps) {
  const { t } = useTranslation();
  const { timeline, progress, deliveryInfo, estimatedDelivery } = tracking;

  return (
    <div className="space-y-6">
      {/* Progress Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">{progress.stepProgress}</span>
          <span className="text-primary font-semibold">{progress.percentage}%</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
          <div
            className="bg-primary h-full rounded-full transition-all duration-500"
            style={{ width: `${progress.percentage}%` }}
          />
        </div>
        {progress.nextMilestone && (
          <p className="text-sm text-muted-foreground">
            {t('orders.nextStep', { defaultValue: 'Prochaine étape' })}: {t(`orders.${progress.nextMilestone}`, { defaultValue: t(`orders.trackingEvents.${progress.nextMilestone}`, { defaultValue: progress.nextMilestone }) })}
          </p>
        )}
      </div>

      {/* Estimated Delivery */}
      {estimatedDelivery && (
        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2">
            <Truck className="size-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {t('orders.estimatedDelivery')}
            </span>
          </div>
          <span className="text-sm font-medium">
            {formatDate(estimatedDelivery, 'EEEE dd MMMM')}
          </span>
        </div>
      )}

      {/* Delivery Info */}
      {deliveryInfo && (deliveryInfo.trackingNumber || deliveryInfo.deliveryCompany) && (
        <div className="p-3 border rounded-lg space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {t('orders.deliveryInfo', { defaultValue: 'Informations de livraison' })}
            </span>
            {deliveryInfo.trackingUrl && (
              <a
                href={deliveryInfo.trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                {t('orders.trackPackage', { defaultValue: 'Suivre le colis' })}
                <ExternalLink className="size-3" />
              </a>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {deliveryInfo.deliveryCompany && (
              <div>
                <span className="text-muted-foreground">{t('orders.carrier', { defaultValue: 'Transporteur' })}:</span>
                <p className="font-medium">{deliveryInfo.deliveryCompany}</p>
              </div>
            )}
            {deliveryInfo.trackingNumber && (
              <div>
                <span className="text-muted-foreground">{t('orders.trackingNumber', { defaultValue: 'N° de suivi' })}:</span>
                <p className="font-medium font-mono text-xs">{deliveryInfo.trackingNumber}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Timeline Events */}
      {timeline && timeline.length > 0 && (
        <div className="pt-2">
          <p className="text-sm font-medium text-muted-foreground mb-4">
            {t('orders.timeline')}
          </p>
          <div>
            {timeline.map((event, index) => (
              <TimelineEvent
                key={event.refId || index}
                event={event}
                isLast={index === timeline.length - 1}
              />
            ))}
          </div>
        </div>
      )}

      {/* No Events */}
      {(!timeline || timeline.length === 0) && (
        <div className="text-center py-4">
          <p className="text-sm text-muted-foreground">
            {t('orders.noTrackingEvents', { defaultValue: 'Aucun événement de suivi' })}
          </p>
        </div>
      )}
    </div>
  );
}
